import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface BookingField {
  key: string;
  label: string;
  type: 'text' | 'date' | 'textarea' | 'select';
  required: boolean;
  predefined: boolean;
  options?: string[];
}

interface BookingExtraFieldsConfig {
  enabled: boolean;
  fields: BookingField[];
}

const PREDEFINED_FIELDS: BookingField[] = [
  { key: 'dni', label: 'DNI', type: 'text', required: false, predefined: true },
  { key: 'edad', label: 'Edad', type: 'text', required: false, predefined: true },
  { key: 'fecha_nacimiento', label: 'Fecha de nacimiento', type: 'date', required: false, predefined: true },
  { key: 'domicilio', label: 'Domicilio', type: 'text', required: false, predefined: true },
  { key: 'obra_social', label: 'Obra Social', type: 'text', required: false, predefined: true },
  { key: 'nro_afiliado', label: 'N° Afiliado', type: 'text', required: false, predefined: true },
  { key: 'motivo_consulta', label: 'Motivo de consulta / Médico derivante', type: 'textarea', required: false, predefined: true },
];

const ProfessionalBookingFieldsConfig: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<BookingExtraFieldsConfig>({ enabled: false, fields: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/professionals/${user.id}/booking-extra-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.booking_extra_fields) {
        setConfig(data.booking_extra_fields);
      }
    } catch (error) {
      console.error('Error fetching booking extra fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/professionals/${user.id}/booking-extra-fields`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ booking_extra_fields: config })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = () => {
    setConfig(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  const toggleField = (key: string) => {
    setConfig(prev => {
      const exists = prev.fields.find(f => f.key === key);
      if (exists) {
        return { ...prev, fields: prev.fields.filter(f => f.key !== key) };
      } else {
        const predefined = PREDEFINED_FIELDS.find(f => f.key === key);
        if (predefined) {
          return { ...prev, fields: [...prev.fields, { ...predefined }] };
        }
        return prev;
      }
    });
  };

  const toggleRequired = (key: string) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.key === key ? { ...f, required: !f.required } : f)
    }));
  };

  const addCustomField = () => {
    if (!newFieldLabel.trim()) return;
    const key = 'custom_' + newFieldLabel.trim().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

    if (config.fields.find(f => f.key === key)) {
      alert('Ya existe un campo con ese nombre');
      return;
    }

    setConfig(prev => ({
      ...prev,
      fields: [...prev.fields, {
        key,
        label: newFieldLabel.trim(),
        type: 'text',
        required: false,
        predefined: false
      }]
    }));
    setNewFieldLabel('');
  };

  const removeCustomField = (key: string) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.key !== key)
    }));
  };

  const isFieldActive = (key: string) => config.fields.some(f => f.key === key);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Datos adicionales del paciente</h1>
            <p className="text-sm text-gray-500">Configura qué información extra pedir al agendar turno</p>
          </div>
        </div>

        {/* Toggle principal */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">Solicitar datos adicionales</h2>
              <p className="text-sm text-gray-500 mt-1">
                Al activarlo, el paciente verá campos extra al agendar su turno
              </p>
            </div>
            <button onClick={toggleEnabled} className="focus:outline-none">
              {config.enabled ? (
                <ToggleRight className="w-10 h-10 text-blue-600" />
              ) : (
                <ToggleLeft className="w-10 h-10 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {config.enabled && (
          <>
            {/* Campos predefinidos */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Campos disponibles</h3>
              <p className="text-sm text-gray-500 mb-4">Selecciona los datos que necesitas del paciente</p>
              <div className="space-y-3">
                {PREDEFINED_FIELDS.map(field => {
                  const active = isFieldActive(field.key);
                  return (
                    <div key={field.key} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${active ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggleField(field.key)} className="focus:outline-none">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${active ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                            {active && <span className="text-white text-xs font-bold">✓</span>}
                          </div>
                        </button>
                        <span className={`text-sm font-medium ${active ? 'text-gray-900' : 'text-gray-600'}`}>{field.label}</span>
                      </div>
                      {active && (
                        <button
                          onClick={() => toggleRequired(field.key)}
                          className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                            config.fields.find(f => f.key === field.key)?.required
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {config.fields.find(f => f.key === field.key)?.required ? 'Obligatorio' : 'Opcional'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Campos personalizados */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Campos personalizados</h3>
              <p className="text-sm text-gray-500 mb-4">Agrega campos propios según tu especialidad</p>

              {/* Lista de campos custom existentes */}
              {config.fields.filter(f => !f.predefined).map(field => (
                <div key={field.key} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50 mb-2">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{field.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleRequired(field.key)}
                      className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                        field.required ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {field.required ? 'Obligatorio' : 'Opcional'}
                    </button>
                    <button onClick={() => removeCustomField(field.key)} className="text-red-400 hover:text-red-600 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Agregar nuevo campo */}
              <div className="flex gap-2 mt-3">
                <input
                  type="text"
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                  placeholder="Ej: Zona de dolor, Alergias..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && addCustomField()}
                />
                <button
                  onClick={addCustomField}
                  disabled={!newFieldLabel.trim()}
                  className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Agregar
                </button>
              </div>
            </div>
          </>
        )}

        {/* Botón guardar */}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors ${
            saved ? 'bg-green-500' : 'bg-blue-600 hover:bg-blue-700'
          } disabled:opacity-50`}
        >
          {saving ? 'Guardando...' : saved ? 'Guardado correctamente' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
};

export default ProfessionalBookingFieldsConfig;
